import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "@/lib/api";
import { Button, Spinner } from "@/components/ui";

/** Sign in — or, on a brand-new studio, make the first account (which becomes the admin). */
export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ["auth-status"], queryFn: api.authStatus });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const setup = status.data?.setupRequired === true;

  const submit = useMutation({
    mutationFn: async () => {
      const me = setup
        ? await api.setup({ username: username.trim(), password, displayName: displayName.trim() || undefined })
        : await api.login(username.trim(), password);
      return me;
    },
    onSuccess: async (me) => {
      queryClient.setQueryData(["me"], me);
      await queryClient.invalidateQueries();
      navigate({ to: "/create", search: { kind: "video" } });
    },
  });

  return (
    <div className="flex h-dvh-screen items-center justify-center bg-desk px-4">
      <div className="w-full max-w-[380px]">
        <p className="font-display text-[22px] tracking-[-0.01em]">studio</p>
        <h1 className="mt-6 text-[26px] font-semibold leading-tight">{setup ? "Make the first account" : "Sign in"}</h1>
        <p className="mt-2 text-[15px] text-mute">
          {setup
            ? "This account manages the studio: it can add people and reserve GPU servers."
            : "Your pictures, videos, songs and chats are yours alone; the GPU queue is shared."}
        </p>

        {status.isLoading ? (
          <div className="mt-8 flex justify-center">
            <Spinner />
          </div>
        ) : (
          <form
            className="mt-6 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              submit.mutate();
            }}
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-mute">Username</span>
              {/* biome-ignore lint/a11y/noAutofocus: the only field on a page people open to type */}
              <input
                autoFocus
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-11 rounded-[var(--radius-control)] border border-line bg-panel px-3 text-[16px] outline-none focus-visible:border-mute"
              />
            </label>
            {setup ? (
              <label className="flex flex-col gap-1.5">
                <span className="text-[13px] font-medium text-mute">Name shown to others</span>
                <input
                  autoComplete="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={username || "Alex"}
                  className="h-11 rounded-[var(--radius-control)] border border-line bg-panel px-3 text-[16px] outline-none placeholder:text-mute/70 focus-visible:border-mute"
                />
              </label>
            ) : null}
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium text-mute">Password</span>
              <input
                type="password"
                autoComplete={setup ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 rounded-[var(--radius-control)] border border-line bg-panel px-3 text-[16px] outline-none focus-visible:border-mute"
              />
            </label>
            {submit.isError ? <p className="text-[14px] text-danger">{(submit.error as Error).message}</p> : null}
            <Button type="submit" variant="primary" className="mt-2 h-11" disabled={submit.isPending || !username.trim() || !password}>
              {submit.isPending ? <Spinner /> : setup ? "Create account" : "Sign in"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
